import { ROUTES } from "@/constants/routes.constants";
import {
  NPS_COLOR_DETRACTOR,
  NPS_COLOR_PASSIVE,
  NPS_COLOR_PROMOTER,
} from "@/components/molecules/NpsRatingScale/nps-rating-scale.styles";
import type { RegistrationFeedbackMetadata } from "./registrationFeedback.types";

export type RegisterFeedbackLocationState = {
  username?: string;
  userUUID?: string;
  loanNumber?: string;
  rating?: number;
};

export function toRegisterFeedbackState(
  metadata: Partial<RegistrationFeedbackMetadata>,
): RegisterFeedbackLocationState {
  const state: RegisterFeedbackLocationState = {};

  if (metadata.email) {
    state.username = metadata.email;
  }
  if (metadata.userUUID) {
    state.userUUID = metadata.userUUID;
  }
  if (metadata.loanNumber) {
    state.loanNumber = metadata.loanNumber;
  }

  return state;
}

export function toRegistrationFeedbackMetadata(
  state: RegisterFeedbackLocationState | null | undefined,
): RegistrationFeedbackMetadata | undefined {
  if (!state?.username || !state.userUUID || !state.loanNumber) {
    return undefined;
  }

  return {
    email: state.username,
    userUUID: state.userUUID,
    loanNumber: state.loanNumber,
  };
}

export function mergeRegisterFeedbackState(
  base: RegisterFeedbackLocationState | undefined,
  updates: RegisterFeedbackLocationState,
): RegisterFeedbackLocationState {
  return { ...base, ...updates };
}

export function getRegisterFeedbackEntryNavigation(
  metadata?: Partial<RegistrationFeedbackMetadata>,
): {
  route: string;
  state?: RegisterFeedbackLocationState;
} {
  const state = metadata ? toRegisterFeedbackState(metadata) : undefined;
  return state && Object.keys(state).length > 0
    ? { route: ROUTES.REGISTER_FEEDBACK, state }
    : { route: ROUTES.REGISTER_FEEDBACK };
}

export function getRegisterFeedbackExitNavigation(username?: string): {
  route: string;
  state?: { username: string };
} {
  return username
    ? { route: ROUTES.REGISTER_SUCCESS, state: { username } }
    : { route: ROUTES.REGISTER_SUCCESS };
}

export function getRegisterFeedbackNavigationAfterRating(
  rating: number,
  feedbackState?: RegisterFeedbackLocationState,
): {
  route: string;
  state: RegisterFeedbackLocationState;
} {
  const needsComment = isFeedbackCommentEligibleRating(rating);
  const route = needsComment
    ? ROUTES.REGISTER_FEEDBACK_COMMENT
    : ROUTES.REGISTER_FEEDBACK_THANK_YOU;
  const state: RegisterFeedbackLocationState = mergeRegisterFeedbackState(feedbackState, { rating });

  return { route, state };
}

export function getRegisterFeedbackThankYouNavigation(
  feedbackState?: RegisterFeedbackLocationState,
  rating?: number,
): {
  route: string;
  state?: RegisterFeedbackLocationState;
} {
  const state = mergeRegisterFeedbackState(feedbackState, {});
  if (rating !== undefined) {
    state.rating = rating;
  }

  return {
    route: ROUTES.REGISTER_FEEDBACK_THANK_YOU,
    state: Object.keys(state).length > 0 ? state : undefined,
  };
}

export function getNpsRatingDisplayColor(rating: number): string {
  if (rating <= 6) return NPS_COLOR_DETRACTOR;
  if (rating >= 9) return NPS_COLOR_PROMOTER;
  return NPS_COLOR_PASSIVE;
}

export function isFeedbackCommentEligibleRating(rating: number): boolean {
  return (rating >= 1 && rating <= 6) || rating >= 9;
}

export function isValidCommentStepRating(rating: number | undefined): rating is number {
  if (rating === undefined) return false;
  return isFeedbackCommentEligibleRating(rating);
}
